/* eslint-disable */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { currencyLabel } from "@/lib/currency";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Loader2, Sparkles, FileText, FileSpreadsheet, FileType2, Check, Pencil, LayoutDashboard, ReceiptText } from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const hasInvoicing = user?.is_admin || ["pro", "business"].includes(user?.plan);
  const [invBusy, setInvBusy] = useState(false);
  const { t, i18n } = useTranslation();
  const cur = currencyLabel(i18n.language);
  const CATS = [
    { v: "materiale", l: t("jobDetail.cat_materiale") },
    { v: "motorina", l: t("jobDetail.cat_motorina") },
    { v: "manopera", l: t("jobDetail.cat_manopera") },
    { v: "transport", l: t("jobDetail.cat_transport") },
    { v: "altele", l: t("jobDetail.cat_altele") },
  ];
  const catLabel = (v) => (CATS.find((c) => c.v === v) || {}).l || v;
  const [job, setJob] = useState(null);
  const [exp, setExp] = useState({ category: "materiale", description: "", quantity: "1", unit_price: "" });
  const [adding, setAdding] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState("");
  const [editMeta, setEditMeta] = useState(false);
  const [meta, setMeta] = useState({ title: "", client_name: "", value: "", scheduled_start: "", scheduled_end: "" });

  const load = async () => {
    try {
      const r = await api.get(`/jobs/${id}`);
      setJob(r.data);
      setMeta({ title: r.data.title, client_name: r.data.client_name || "", value: r.data.value, scheduled_start: (r.data.scheduled_start || "").slice(0, 10), scheduled_end: (r.data.scheduled_end || "").slice(0, 10) });
    } catch (e) {
      toast.error(t("jobDetail.jobNotFound"));
      nav("/app/lucrari");
    }
  };
  useEffect(() => { load(); }, [id]);

  const addExpense = async (e) => {
    e.preventDefault();
    if (!exp.description.trim()) return toast.error(t("jobDetail.addDescErr"));
    setAdding(true);
    try {
      const r = await api.post(`/jobs/${id}/expenses`, {
        category: exp.category, description: exp.description,
        quantity: parseFloat(exp.quantity) || 1, unit_price: parseFloat(exp.unit_price) || 0,
      });
      setJob(r.data);
      setExp({ category: "materiale", description: "", quantity: "1", unit_price: "" });
    } catch (e) {
      toast.error(t("jobDetail.addErr"));
    } finally {
      setAdding(false);
    }
  };

  const delExpense = async (eid) => {
    try {
      const r = await api.delete(`/jobs/${id}/expenses/${eid}`);
      setJob(r.data);
    } catch (e) { toast.error(t("jobDetail.delErr")); }
  };

  const saveMeta = async () => {
    try {
      const r = await api.put(`/jobs/${id}`, {
        title: meta.title, client_name: meta.client_name, value: parseFloat(meta.value) || 0,
        scheduled_start: meta.scheduled_start ? `${meta.scheduled_start}T12:00:00.000Z` : null,
        scheduled_end: meta.scheduled_end ? `${meta.scheduled_end}T12:00:00.000Z` : null,
      });
      setJob(r.data);
      setEditMeta(false);
      toast.success(t("jobDetail.saved"));
    } catch (e) { toast.error(t("jobDetail.saveErr")); }
  };

  const toggleStatus = async () => {
    const next = job.status === "completed" ? "in_progress" : "completed";
    try {
      const r = await api.put(`/jobs/${id}`, { status: next });
      setJob(r.data);
    } catch (e) { toast.error(t("jobDetail.error")); }
  };

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/jobs/${id}/ai-summary?lang=${i18n.language}`);
      setJob({ ...job, ai_summary: r.data.summary });
      toast.success(t("jobDetail.aiGenerated"));
    } catch (e) {
      toast.error(t("jobDetail.aiError"));
    } finally { setAiBusy(false); }
  };

  const downloadReport = async (fmt) => {
    setReportBusy(fmt);
    try {
      const res = await api.get(`/jobs/${id}/report?format=${fmt}&with_ai=true&lang=${i18n.language}`, { responseType: "blob" });
      const map = { pdf: "application/pdf", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" };
      const url = window.URL.createObjectURL(new Blob([res.data], { type: map[fmt] }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(job?.title || "lucrare").replace(/[^a-z0-9]+/gi, "_")}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      load();
    } catch (e) {
      toast.error(t("jobDetail.reportErr"));
    } finally { setReportBusy(""); }
  };

  const emitInvoice = async () => {
    setInvBusy(true);
    try {
      const r = await api.post(`/invoices/from-job/${id}?kind=factura`);
      toast.success(t("jobDetail.invoiceCreated"));
      nav(`/app/facturare/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally { setInvBusy(false); }
  };

  if (!job) return <div className="p-10 text-sm text-[#94A3B8]">{t("jobDetail.loading")}</div>;
  const tot = job.totals || {};
  const positive = (tot.profit || 0) >= 0;

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="job-detail">
      <Link to="/app/lucrari" className="text-sm text-[#94A3B8] hover:text-[#00BFFF] flex items-center gap-1"><ArrowLeft size={14} /> {t("jobDetail.back")}</Link>

      {/* Header */}
      <div className="mt-4 card-flat p-6">
        {editMeta ? (
          <div className="space-y-3">
            <input className="input-flat" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} data-testid="edit-title" />
            <div className="grid grid-cols-2 gap-3">
              <input className="input-flat" placeholder={t("jobDetail.clientPh")} value={meta.client_name} onChange={(e) => setMeta({ ...meta, client_name: e.target.value })} data-testid="edit-client" />
              <input className="input-flat" type="number" step="0.01" placeholder={t("jobDetail.valuePh")} value={meta.value} onChange={(e) => setMeta({ ...meta, value: e.target.value })} data-testid="edit-value" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">{t("jobDetail.schedFrom")}</label>
                <input className="input-flat" type="date" value={meta.scheduled_start} onChange={(e) => setMeta({ ...meta, scheduled_start: e.target.value })} data-testid="edit-sched-start" />
              </div>
              <div>
                <label className="overline block mb-1">{t("jobDetail.schedTo")}</label>
                <input className="input-flat" type="date" value={meta.scheduled_end} onChange={(e) => setMeta({ ...meta, scheduled_end: e.target.value })} data-testid="edit-sched-end" />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={saveMeta} className="btn-primary" data-testid="save-meta">{t("jobDetail.save")}</button>
              <button onClick={() => setEditMeta(false)} className="btn-outline">{t("jobDetail.cancel")}</button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight">{job.title}</h1>
              <p className="text-sm text-[#94A3B8] mt-1">{job.client_name || "—"}</p>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`/app/lucrari/${id}/proiect`} className="btn-primary flex items-center gap-2" data-testid="open-project-btn"><LayoutDashboard size={15} /> {t("jobDetail.openPM")}</Link>
              {hasInvoicing && (
                <button onClick={emitInvoice} disabled={invBusy} className="btn-outline flex items-center gap-2 disabled:opacity-50" data-testid="emit-invoice-btn">
                  {invBusy ? <Loader2 size={15} className="animate-spin" /> : <ReceiptText size={15} />} {t("jobDetail.emitInvoice")}
                </button>
              )}
              <button onClick={() => setEditMeta(true)} className="btn-outline flex items-center gap-2" data-testid="edit-meta-btn"><Pencil size={15} /> {t("jobDetail.edit")}</button>
              <button onClick={toggleStatus} className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${job.status === "completed" ? "bg-[#22C55E] text-white border-[#22C55E]" : "bg-[#0B111D] border-[#1E293B] hover:border-[#22C55E]"}`} data-testid="toggle-status-btn">
                <Check size={15} /> {job.status === "completed" ? t("jobDetail.completed") : t("jobDetail.markCompleted")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Profit summary */}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="job-summary">
        <div className="card-flat p-4"><div className="text-[10px] uppercase text-[#94A3B8]">{t("jobDetail.jobValue")}</div><div className="font-display text-2xl font-extrabold mt-1">{(tot.value ?? 0).toFixed(2)}</div><div className="text-xs text-[#94A3B8]">{cur}</div></div>
        <div className="card-flat p-4"><div className="text-[10px] uppercase text-[#94A3B8]">{t("jobDetail.totalExpenses")}</div><div className="font-display text-2xl font-extrabold mt-1">{(tot.total_expenses ?? 0).toFixed(2)}</div><div className="text-xs text-[#94A3B8]">{cur}</div></div>
        <div className={`p-4 ${positive ? "bg-[#22C55E]" : "bg-[#EF4444]"} text-white`} data-testid="job-profit"><div className="text-[10px] uppercase opacity-80">{t("jobDetail.profit")}</div><div className="font-display text-2xl font-extrabold mt-1">{(tot.profit ?? 0).toFixed(2)}</div><div className="text-xs opacity-80">{cur}</div></div>
        <div className="card-flat p-4"><div className="text-[10px] uppercase text-[#94A3B8]">{t("jobDetail.margin")}</div><div className={`font-display text-2xl font-extrabold mt-1 ${positive ? "text-[#22C55E]" : "text-[#EF4444]"}`}>{(tot.margin_percent ?? 0)}%</div></div>
      </div>

      {/* Add expense */}
      <div className="mt-6 card-flat p-6">
        <div className="overline mb-3">{t("jobDetail.addExpense")}</div>
        <form onSubmit={addExpense} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end" data-testid="expense-form">
          <div className="sm:col-span-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("jobDetail.category")}</label>
            <select className="input-flat mt-1" value={exp.category} onChange={(e) => setExp({ ...exp, category: e.target.value })} data-testid="expense-category">
              {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <div className="sm:col-span-4">
            <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("jobDetail.description")}</label>
            <input className="input-flat mt-1" value={exp.description} onChange={(e) => setExp({ ...exp, description: e.target.value })} placeholder={t("jobDetail.descPh")} data-testid="expense-desc" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("jobDetail.qty")}</label>
            <input className="input-flat mt-1" type="number" step="0.01" value={exp.quantity} onChange={(e) => setExp({ ...exp, quantity: e.target.value })} data-testid="expense-qty" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("jobDetail.unitPrice")}</label>
            <input className="input-flat mt-1" type="number" step="0.01" value={exp.unit_price} onChange={(e) => setExp({ ...exp, unit_price: e.target.value })} data-testid="expense-price" />
          </div>
          <div className="sm:col-span-1">
            <button type="submit" disabled={adding} className="btn-primary w-full flex items-center justify-center disabled:opacity-50" data-testid="add-expense-btn">
              {adding ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
            </button>
          </div>
        </form>
      </div>

      {/* Expenses list */}
      <div className="mt-4 card-flat overflow-hidden" data-testid="expenses-list">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#111C30] text-white text-left">
            <th className="p-3">{t("jobDetail.category")}</th><th className="p-3">{t("jobDetail.description")}</th><th className="p-3 text-right">{t("jobDetail.qty")}</th><th className="p-3 text-right">{t("jobDetail.unitPrice")}</th><th className="p-3 text-right">{t("jobDetail.sum")}</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {(job.expenses || []).length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-[#94A3B8]">{t("jobDetail.expensesEmpty")}</td></tr>
            ) : job.expenses.map((e) => (
              <tr key={e.id} className="border-b border-[#1E293B]" data-testid={`expense-${e.id}`}>
                <td className="p-3">{catLabel(e.category)}</td>
                <td className="p-3">{e.description}</td>
                <td className="p-3 text-right font-mono-plex">{(e.quantity ?? 1).toFixed(2)}</td>
                <td className="p-3 text-right font-mono-plex">{(e.unit_price ?? 0).toFixed(2)}</td>
                <td className="p-3 text-right font-mono-plex font-semibold">{(e.amount ?? 0).toFixed(2)}</td>
                <td className="p-3 text-right"><button onClick={() => delExpense(e.id)} className="text-[#94A3B8] hover:text-[#EF4444]" data-testid={`del-expense-${e.id}`}><Trash2 size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI summary */}
      <div className="mt-6 card-flat p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="overline">{t("jobDetail.aiAnalysis")}</div>
          <button onClick={generateAI} disabled={aiBusy} className="btn-ink flex items-center gap-2 disabled:opacity-50" data-testid="generate-ai-btn">
            {aiBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {job.ai_summary ? t("jobDetail.regenerate") : t("jobDetail.generateAnalysis")}
          </button>
        </div>
        {job.ai_summary ? (
          <p className="mt-3 text-sm leading-relaxed text-[#E2E8F0]" data-testid="ai-summary-text">{job.ai_summary}</p>
        ) : (
          <p className="mt-3 text-sm text-[#94A3B8]">{t("jobDetail.aiPlaceholder")}</p>
        )}
      </div>

      {/* Reports */}
      <div className="mt-6 card-flat p-6">
        <div className="overline mb-3">{t("jobDetail.downloadReport")}</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => downloadReport("pdf")} disabled={!!reportBusy} className="btn-outline flex items-center gap-2 disabled:opacity-50" data-testid="report-pdf">
            {reportBusy === "pdf" ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} PDF
          </button>
          <button onClick={() => downloadReport("xlsx")} disabled={!!reportBusy} className="btn-outline flex items-center gap-2 disabled:opacity-50" data-testid="report-xlsx">
            {reportBusy === "xlsx" ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />} Excel
          </button>
          <button onClick={() => downloadReport("docx")} disabled={!!reportBusy} className="btn-outline flex items-center gap-2 disabled:opacity-50" data-testid="report-docx">
            {reportBusy === "docx" ? <Loader2 size={16} className="animate-spin" /> : <FileType2 size={16} />} Word
          </button>
        </div>
        <p className="text-xs text-[#94A3B8] mt-3">{t("jobDetail.reportHint")}</p>
      </div>
    </div>
  );
}
